import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Lock, Eye, EyeOff, Mail, CheckCircle, AlertTriangle, Loader2 } from "lucide-react";

// Imports do Firebase
import { createUserWithEmailAndPassword, deleteUser } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../config/firebase";

const SetupInterno = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandColor = "#00a99d";

  const emailParam = (searchParams.get("email") || "").trim().toLowerCase();
  const inviteToken = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);

  useEffect(() => {
    if (!id || !emailParam || !inviteToken) {
      setError("Convite inválido ou incompleto.");
    }
    setLoading(false);
  }, [id, emailParam, inviteToken]);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(6, "A senha deve ter pelo menos 6 caracteres")
        .required("A senha é obrigatória"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], "As senhas não coincidem")
        .required("Confirme sua senha"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      let createdUser = null;
      let createdInThisAttempt = false;

      try {
        if (!id || !emailParam || !inviteToken) {
          throw new Error("Convite inválido ou incompleto.");
        }

        // 1. Reutiliza a identidade autenticada em retries após
        // falhas transitórias; caso contrário cria o Firebase Auth.
        const currentUserEmail = String(auth.currentUser?.email || "")
          .trim()
          .toLowerCase();

        if (auth.currentUser && currentUserEmail === emailParam) {
          createdUser = auth.currentUser;
        } else {
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            emailParam,
            values.password
          );
          createdUser = userCredential.user;
          createdInThisAttempt = true;
        }

        // 2. A autoridade de role/isInternal fica exclusivamente no backend.
        const activateInternalUser = httpsCallable(functions, "activateInternalUser");
        await activateInternalUser({
          teamInviteId: id,
          inviteToken,
          environment: process.env.REACT_APP_ENV
        });

        setSetupComplete(true);
        
        setTimeout(() => {
          navigate("/super-admin/dashboard");
        }, 3000);

      } catch (err) {
        const error = /** @type {any} */ (err);
        console.error("Erro na criação do acesso:", error);
        if (error.code === "auth/email-already-in-use") {
          alert("Este e-mail já possui um cadastro ativo no sistema.");
        } else {
          const deterministicFailure = [
            "functions/permission-denied",
            "functions/invalid-argument",
            "functions/not-found",
            "functions/failed-precondition"
          ].includes(error.code);

          if (createdUser && createdInThisAttempt && deterministicFailure) {
            try {
              await deleteUser(createdUser);
            } catch (rollbackError) {
              console.error("Falha ao remover identidade após rejeição do convite:", rollbackError);
            }
          }

          alert(`Erro ao criar acesso: ${error.message}`);
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 font-sans">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl border border-gray-100 text-center animate-fade-in">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acesso Indisponível</h2>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <button onClick={() => navigate("/login")} className="px-6 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-gray-800 transition-colors">
            Ir para o Login
          </button>
        </div>
      </div>
    );
  }

  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 font-sans">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl border border-gray-100 text-center animate-fade-in">
          <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-teal-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Tudo pronto!</h2>
          <p className="text-gray-500 text-sm mb-6">Sua senha foi configurada e seu acesso está ativo. Redirecionando para o seu painel interno...</p>
          <Loader2 className="w-6 h-6 text-teal-600 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 font-sans">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in">
        
        {/* HEADER DA MARCA COM LOGOTIPO REAL */}
        <div className="p-8 text-center border-b border-gray-50 bg-white">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="Real Benefícios" 
              className="h-10 object-contain" 
              onError={(e) => {
                const target = /** @type {HTMLImageElement} */ (e.target);
                target.style.display = 'none';
                if (target.nextElementSibling) {
                  const fallbackText = /** @type {HTMLElement} */ (target.nextElementSibling);
                  fallbackText.style.display = 'block';
                }
              }}
            />
            {/* Fallback em texto escondido */}
            <div style={{ display: 'none' }} className="text-2xl font-black tracking-tight text-gray-900">Real Benefícios</div>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Configure seu acesso</h2>
          <p className="text-sm text-gray-500 mt-1">Defina uma senha segura para acessar o painel da equipe interna.</p>
        </div>

        {/* FORMULÁRIO */}
        <div className="p-8 bg-gray-50/30">
          <form onSubmit={formik.handleSubmit} className="space-y-5">
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">E-mail (Seu login)</label>
                <div className="flex items-center px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-600 text-sm font-medium">
                  <Mail className="w-4 h-4 mr-3 text-gray-400" />
                  {emailParam}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">Criar Senha <span className="text-red-500">*</span></label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <Lock className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  {...formik.getFieldProps("password")}
                  className={`w-full pl-11 pr-12 py-3 bg-white border text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all shadow-sm ${formik.touched.password && formik.errors.password ? 'border-red-500' : 'border-gray-200'}`}
                  placeholder="Mínimo 6 caracteres"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-teal-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formik.touched.password && formik.errors.password && <p className="mt-1.5 text-xs font-bold text-red-500">{String(formik.errors.password)}</p>}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">Confirmar Senha <span className="text-red-500">*</span></label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <Lock className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  {...formik.getFieldProps("confirmPassword")}
                  className={`w-full pl-11 pr-12 py-3 bg-white border text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all shadow-sm ${formik.touched.confirmPassword && formik.errors.confirmPassword ? 'border-red-500' : 'border-gray-200'}`}
                  placeholder="Repita a senha"
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-teal-600">
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formik.touched.confirmPassword && formik.errors.confirmPassword && <p className="mt-1.5 text-xs font-bold text-red-500">{String(formik.errors.confirmPassword)}</p>}
            </div>

            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="w-full py-3.5 mt-4 text-sm font-bold text-white rounded-xl transition-all shadow-lg hover:brightness-110 disabled:opacity-50 flex items-center justify-center"
              style={{ backgroundColor: brandColor }}
            >
              {formik.isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ativar Acesso e Entrar"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetupInterno;